import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart deve ser usado dentro de CartProvider');
  }
  return context;
};

// Modelo de pricing alinhado com a Documentação Funcional v1.0:
// - price: preço cheio CONSUMER (sempre presente).
// - resellerPrice: preço com desconto para RESELLER (presente quando aplicável).
// - unitPrice: preço efetivamente pago no item (= resellerPrice se reseller, senão price).
// - unitDiscount: desconto recebido pelo reseller (= price - resellerPrice; 0 para CONSUMER).

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [supplierId, setSupplierId] = useState(null);
  // Cupom aplicado no carrinho. Persistido junto com items pra não perder
  // entre reloads. Forma: `{ code, type, discountAmount, freeShipping }`.
  const [coupon, setCoupon] = useState(null);
  // Frete escolhido. Forma: `{ cep, provider, name, price, etaDays }`.
  // Persistido para sobreviver navegação carrinho ↔ checkout.
  const [shipping, setShipping] = useState(null);

  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        setItems(parsed.items || []);
        setSupplierId(parsed.supplierId || null);
        setCoupon(parsed.coupon || null);
        setShipping(parsed.shipping || null);
      } catch (error) {
        console.error('Erro ao carregar carrinho:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify({ items, supplierId, coupon, shipping }));
  }, [items, supplierId, coupon, shipping]);

  const addItem = (product, variation, quantity = 1, options = {}) => {
    const { isReseller = false } = options;
    setItems(prevItems => {
      if (supplierId && product.supplierId !== supplierId) {
        throw new Error('Não é possível adicionar produtos de fornecedores diferentes no mesmo pedido');
      }

      if (!supplierId) {
        setSupplierId(product.supplierId);
      }

      const price = Number(variation.price) || 0;
      const resellerPrice = variation.resellerPrice != null ? Number(variation.resellerPrice) : null;
      const unitPrice = (isReseller && resellerPrice != null && resellerPrice > 0) ? resellerPrice : price;
      const unitDiscount = Math.max(0, Math.round((price - unitPrice) * 100) / 100);

      const existingIndex = prevItems.findIndex(
        item => item.productId === product.id && item.variationId === variation.id
      );

      if (existingIndex >= 0) {
        const updated = [...prevItems];
        updated[existingIndex].quantity += quantity;
        return updated;
      }

      return [...prevItems, {
        id: `${product.id}-${variation.id}`,
        productId: product.id,
        productName: product.name,
        variationId: variation.id,
        variationName: variation.name,
        price,
        resellerPrice,
        unitPrice,
        unitDiscount,
        commissionPercent: variation.commissionPercent != null ? Number(variation.commissionPercent) : 0,
        quantity,
        image: product.images?.[0] || product.image
      }];
    });
  };

  const removeItem = (itemId) => {
    setItems(prevItems => {
      const filtered = prevItems.filter(item => item.id !== itemId);
      if (filtered.length === 0) {
        setSupplierId(null);
      }
      return filtered;
    });
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
    setSupplierId(null);
    setCoupon(null);
    setShipping(null);
  };

  const applyCoupon = (couponData) => setCoupon(couponData);
  const clearCoupon = () => setCoupon(null);

  const setShippingOption = (option) => setShipping(option);
  const clearShipping = () => setShipping(null);

  const getTotalItems = () =>
    items.reduce((sum, item) => sum + item.quantity, 0);

  // Soma dos preços efetivamente pagos (pré-frete).
  const getSubtotal = () =>
    items.reduce((sum, item) => sum + ((item.unitPrice ?? item.price) * item.quantity), 0);

  // Soma dos descontos recebidos pelo reseller. Zero para CONSUMER.
  const getTotalDiscount = () =>
    items.reduce((sum, item) => sum + ((item.unitDiscount ?? 0) * item.quantity), 0);

  // Desconto aplicado pelo cupom. Ignorado se o subtotal mudou abaixo do
  // mínimo após validar — UI deve revalidar quando items mudarem.
  const getCouponDiscount = () => {
    if (!coupon) return 0;
    if (coupon.type === 'FREE_SHIPPING') return 0;
    return Math.min(getSubtotal(), Number(coupon.discountAmount) || 0);
  };

  // Frete a cobrar. Cupom FREE_SHIPPING zera frete.
  const getShippingCost = () => {
    if (coupon?.type === 'FREE_SHIPPING' || coupon?.freeShipping) return 0;
    return Number(shipping?.price) || 0;
  };

  // Total final = subtotal − desconto cupom + frete.
  const getTotalToPay = () =>
    Math.max(0, getSubtotal() - getCouponDiscount() + getShippingCost());

  const value = {
    items,
    supplierId,
    coupon,
    shipping,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    applyCoupon,
    clearCoupon,
    setShippingOption,
    clearShipping,
    getTotalItems,
    getSubtotal,
    getTotalDiscount,
    getCouponDiscount,
    getShippingCost,
    getTotalCommission: getTotalDiscount, // alias de compat para callers antigos
    getTotalToPay,
    isEmpty: items.length === 0
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
